<template>
  <TaskFocusView
    v-if="isFocusView"
    :store="store"
    :projects="projects"
    :initial-task-id="initialTaskId"
    :workspace-slug="workspaceSlug"
    :workspace-name="workspaceName"
    :current-user-label="currentUserLabel"
    :is-workspace-owner="isWorkspaceOwner"
  />

  <main v-else :class="mainShellClass">
    <header :class="headerClass">
      <div>
        <p class="text-sm font-medium text-slate-500 dark:text-slate-400">{{ workspaceSlug }}</p>
        <div class="mt-1 flex flex-wrap items-center gap-3">
          <h1 class="text-2xl font-semibold tracking-normal text-slate-950 dark:text-slate-100">
            {{ workspaceName || 'Tasks' }}
          </h1>
          <ShipMeterChip
            v-if="health"
            :health="health"
            :workspace-slug="workspaceSlug"
            @badge-created="handleBadgeCreated"
          />
        </div>
      </div>

      <div class="flex flex-wrap items-center gap-2">
        <ShipButton
          v-if="health && health.mode === 'building'"
          :health="health"
          :workspace-slug="workspaceSlug"
          @shipped="handleShipped"
        />

        <Dropdown
          :model-value="store.projectFilterId.value"
          :options="projectFilterOptions"
          :trigger-class="projectFilterTriggerClass"
          align="right"
          @update:model-value="store.setProjectFilter"
        >
          <template #trigger>
            <span class="truncate">{{ selectedProjectFilterLabel }}</span>
            <svg class="h-3.5 w-3.5 shrink-0 text-slate-400 dark:text-slate-500" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
              <path d="M4.5 6 8 9.5 11.5 6h-7Z" />
            </svg>
          </template>
          <template #option="{ option, selected }">
            <span class="min-w-0 flex-1 truncate">{{ option.label }}</span>
            <span v-if="selected" class="text-xs font-semibold text-indigo-700 dark:text-indigo-400">Selected</span>
          </template>
        </Dropdown>

        <div class="inline-flex w-fit border border-slate-300 bg-slate-50 p-1 shadow-sm dark:border-slate-700 dark:bg-slate-900">
          <button
            type="button"
            :class="tabButtonClass('list')"
            @click="activeTab = 'list'"
          >
            List
          </button>
          <button
            type="button"
            :class="tabButtonClass('board')"
            @click="activeTab = 'board'"
          >
            Board
          </button>
        </div>
      </div>
    </header>

    <div v-if="store.error.value" class="mx-6 mt-4 border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-900/60 dark:bg-red-950/40 dark:text-red-300">
      {{ store.error.value }}
    </div>

    <div v-if="activeTab === 'list'" class="flex items-start gap-6 px-6 py-5">
      <div class="min-w-0 flex-1">
        <TaskList
          :store="store"
          :projects="projects"
        />
      </div>

      <aside class="hidden w-80 shrink-0 space-y-3 lg:block">
        <ShipMeterCard
          v-if="health"
          :health="health"
          :store="store"
          :workspace-slug="workspaceSlug"
          @changed="handleMeterChanged"
        />

        <section class="border border-slate-200 bg-slate-50 p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900 dark:shadow-black/20">
          <p class="text-xs font-semibold uppercase tracking-normal text-slate-500 dark:text-slate-400">Workspace</p>
          <h2 class="mt-1 truncate text-base font-semibold text-slate-950 dark:text-slate-100">
            {{ workspaceName || workspaceSlug }}
          </h2>

          <div class="mt-4 flex items-center gap-4 text-sm">
            <div>
              <p class="font-semibold text-slate-950 dark:text-slate-100">{{ activeTaskCount }}</p>
              <p class="mt-0.5 text-xs text-slate-500 dark:text-slate-400">Active tasks</p>
            </div>
            <div>
              <p class="font-semibold text-slate-950 dark:text-slate-100">{{ projects.length }}</p>
              <p class="mt-0.5 text-xs text-slate-500 dark:text-slate-400">Projects</p>
            </div>
          </div>

          <div v-if="recentProject" class="mt-4 border border-slate-200 bg-slate-50 px-3 py-2 dark:border-slate-800 dark:bg-slate-800">
            <p class="text-xs font-medium text-slate-500 dark:text-slate-400">Recent project</p>
            <p class="mt-0.5 truncate text-sm font-medium text-slate-900 dark:text-slate-100">{{ recentProject.name }}</p>
          </div>
        </section>

        <section class="border border-slate-200 bg-slate-50 p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900 dark:shadow-black/20">
          <div class="flex items-center justify-between gap-3">
            <h2 class="text-sm font-semibold text-slate-950 dark:text-slate-100">Working on</h2>
            <span class="border border-slate-200 bg-slate-50 px-2 py-0.5 text-xs font-medium text-slate-500 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-400">
              {{ workingOnTasks.length }}
            </span>
          </div>

          <div v-if="workingOnTasks.length" class="mt-3 space-y-2">
            <button
              v-for="task in workingOnTasks"
              :key="task.id"
              type="button"
              class="block w-full border border-slate-200 bg-slate-50 px-3 py-2 text-left hover:border-slate-300 hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:border-slate-800 dark:bg-slate-800 dark:hover:border-slate-700 dark:hover:bg-slate-800/80"
              @click="store.openTask(task.id)"
            >
              <span class="block truncate text-sm font-medium text-slate-950 dark:text-slate-100">{{ task.title }}</span>
              <span class="mt-1 block truncate text-xs text-slate-500 dark:text-slate-400">
                {{ task.locked_by_label || 'Locked' }}
              </span>
            </button>
          </div>
        </section>
      </aside>
    </div>

    <template v-else>
      <!-- Board view is temporary. Do not grow it here; it will move to a different framework as a second screen. -->
      <TaskBoard
        :store="store"
      />
    </template>

    <TaskDetailDrawer
      :store="store"
      :projects="projects"
      :initial-task-id="initialTaskId"
      :current-user-label="currentUserLabel"
      :is-workspace-owner="isWorkspaceOwner"
    />
  </main>
</template>

<script setup>
import { computed, onMounted, ref, watch } from 'vue'
import { useApi } from '../../composables/useApi'
import { usePolling } from '../../composables/usePolling.js'
import { useTaskStore } from '../../composables/useTaskStore'
import ShipButton from './ShipButton.vue'
import ShipMeterCard from './ShipMeterCard.vue'
import ShipMeterChip from './ShipMeterChip.vue'
import TaskBoard from './TaskBoard.vue'
import TaskDetailDrawer from './TaskDetailDrawer.vue'
import TaskFocusView from './TaskFocusView.vue'
import TaskList from './TaskList.vue'
import Dropdown from './ui/Dropdown.vue'

const props = defineProps({
  workspaceSlug: {
    type: String,
    required: true,
  },
  workspaceName: {
    type: String,
    default: '',
  },
  initialView: {
    type: String,
    default: 'list',
    validator: (value) => ['list', 'board'].includes(value),
  },
  initialTaskId: {
    type: [String, Number],
    default: null,
  },
  detailMode: {
    type: String,
    default: 'drawer',
    validator: (value) => ['drawer', 'focus'].includes(value),
  },
  projects: {
    type: Array,
    default: () => [],
  },
  moveTargets: {
    type: Array,
    default: () => [],
  },
  currentUserLabel: {
    type: String,
    default: '',
  },
  isWorkspaceOwner: {
    type: Boolean,
    default: false,
  },
})

const api = useApi()
const store = useTaskStore(props.workspaceSlug, { projects: props.projects, moveTargets: props.moveTargets })
const activeTab = ref(props.initialView === 'board' ? 'board' : 'list')
const health = ref(null)
let lastHealthFetchDate = null
const polling = usePolling(pollTasksAndHealth, { intervalMs: 10000 })
const isFocusView = computed(() => props.detailMode === 'focus')
const allTasks = computed(() => store.filteredTasks.value)
const activeTaskCount = computed(() => allTasks.value.filter((task) => !['done', 'cancelled'].includes(task.status)).length)
const projectFilterOptions = computed(() => [
  { value: null, label: 'All projects' },
  ...store.projects.value.map((project) => ({ value: String(project.id), label: project.name })),
])
const selectedProject = computed(() => {
  const selectedProjectId = store.projectFilterId.value
  if (!selectedProjectId) return null

  return store.projects.value.find((project) => String(project.id) === String(selectedProjectId)) || null
})
const selectedProjectFilterLabel = computed(() => selectedProject.value?.name || 'All projects')
const workingOnTasks = computed(() => {
  return [...allTasks.value]
    .filter((task) => task.locked)
    .sort((a, b) => timestamp(b.locked_at || b.updated_at) - timestamp(a.locked_at || a.updated_at))
    .slice(0, 4)
})
const recentProject = computed(() => {
  const recentTaskWithProject = [...allTasks.value]
    .sort((a, b) => timestamp(b.updated_at) - timestamp(a.updated_at))
    .find((task) => task.project)

  return recentTaskWithProject?.project || selectedProject.value || props.projects[0] || null
})
const taskHealthFingerprint = computed(() => {
  return store.tasks.value.map((task) => [
    task.id,
    task.status,
    task.planning_state,
    task.gut_score,
    task.updated_at,
  ].join(':')).join('|')
})
const mainShellClass = computed(() => {
  if (activeTab.value === 'board') {
    return 'flex h-screen min-h-0 flex-col overflow-hidden bg-slate-50 dark:bg-slate-950'
  }

  return 'min-h-screen bg-slate-50 dark:bg-slate-950'
})
const headerClass = computed(() => [
  'flex flex-col gap-4 border-b border-slate-200 bg-slate-50 dark:border-slate-800 dark:bg-slate-950 sm:flex-row sm:items-center sm:justify-between',
  activeTab.value === 'board' ? 'shrink-0 px-5 py-3' : 'px-6 py-4',
])
const projectFilterTriggerClass = [
  'inline-flex h-10 min-w-40 max-w-56 items-center justify-between gap-2 border border-slate-300 bg-slate-50 px-3 text-left text-sm font-semibold text-slate-700 shadow-sm outline-none hover:bg-slate-100 focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2',
  'dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800 dark:focus:ring-offset-slate-950',
]

onMounted(() => {
  if (isFocusView.value) return

  store.loadTasks().catch(() => {})
  loadHealth()
  polling.start()

  if (props.initialTaskId !== null && props.initialTaskId !== undefined) {
    store.openTask(props.initialTaskId)
  }
})

watch(activeTab, (nextTab) => {
  if (isFocusView.value) return

  const url = new URL(window.location.href)
  url.searchParams.set('view', nextTab)
  history.replaceState(null, '', `${url.pathname}${url.search}${url.hash}`)
})

watch(taskHealthFingerprint, () => {
  if (isFocusView.value) return

  loadHealth()
})

watch(
  () => props.moveTargets,
  (moveTargets) => store.setMoveTargets?.(moveTargets),
  { immediate: true }
)

async function loadHealth() {
  try {
    health.value = await api.getWorkspaceHealth(props.workspaceSlug)
    lastHealthFetchDate = localCalendarDate()
  } catch (error) {
    console.error('Failed to load workspace health', error)
  }
}

async function pollTasksAndHealth() {
  await store.refresh()

  if (lastHealthFetchDate !== localCalendarDate()) {
    await loadHealth()
  }
}

function handleBadgeCreated(badge) {
  if (!health.value) return

  health.value = {
    ...health.value,
    badge,
  }
}

function handleShipped(payload) {
  health.value = payload
  lastHealthFetchDate = localCalendarDate()
}

async function handleMeterChanged() {
  await store.refresh()
  await loadHealth()
}

function timestamp(value) {
  return value ? new Date(value).getTime() : 0
}

function localCalendarDate() {
  return new Date().toDateString()
}

function tabButtonClass(tab) {
  const active = activeTab.value === tab

  return [
    'px-4 py-2 text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 dark:focus:ring-offset-slate-950',
    active ? 'bg-indigo-600 text-slate-50 shadow-sm' : 'text-slate-700 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:text-slate-100',
  ]
}
</script>
